import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Edit, FileSpreadsheet, Plus, Star, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, euro, imgSrc, errMsg, CATEGORIES } from "@/lib/api";
import { ProductForm } from "@/components/admin/ProductForm";

const CATS = [["ropa", "Ropa"], ["calzado", "Calzado"], ["accesorios", "Complementos"]];

export const ProductsTab = () => {
  const call = useApi();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [section, setSection] = useState("all");
  const [cat, setCat] = useState("all");
  const load = useCallback(() => call("get", "/admin/products").then((r) => setList(r.data)), [call]);
  useEffect(() => { load(); }, [load]);

  const del = async () => {
    try { await call("delete", `/admin/products/${toDelete.id}`); toast.success("Producto eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  const toggleStar = async (p) => {
    try {
      await call("patch", `/admin/products/${p.id}/featured`, { featured: !p.featured });
      setList((l) => l.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x)));
      toast.success(!p.featured ? (p.outlet ? "Destacado: saldrá el primero en el outlet" : "Destacado en portada") : "Ya no está destacado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importXlsx = (endpoint, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading(`Importando ${label}…`);
    try {
      const { data } = await call("post", endpoint, fd);
      toast.success(`${data.updated} actualizados · ${data.created} nuevos${data.created ? " (ocultos hasta tener foto y stock)" : ""}`, { id: t });
      load();
    } catch (err) { toast.error(errMsg(err, "Error al importar"), { id: t }); }
    e.target.value = "";
  };

  const term = q.trim().toLowerCase();
  const inSection = (p) => section === "all" || (section === "outlet" ? !!p.outlet : !p.outlet);
  const inCat = (p) => cat === "all" || p.category === cat;
  const inView = (p) => view === "all" || (view === "hidden" ? !p.active : p.active);
  const shown = list.filter((p) => inSection(p) && inCat(p) && inView(p)
    && (!term || `${p.name} ${p.reference || ""} ${(p.outlet_refs || []).join(" ")} ${p.brand}`.toLowerCase().includes(term)));
  const n = (fn) => list.filter(fn).length;
  const sel = "h-10 border rounded-full px-3 text-sm bg-white";
  const [selected, setSelected] = useState(() => new Set());
  const allShownSelected = shown.length > 0 && shown.every((p) => selected.has(p.id));
  const toggleSel = (id) => setSelected((s) => { const c = new Set(s); c.has(id) ? c.delete(id) : c.add(id); return c; });
  const toggleAll = () => setSelected((s) => { const c = new Set(s); shown.forEach((p) => (allShownSelected ? c.delete(p.id) : c.add(p.id))); return c; });
  const exportSlides = async (ids = [...selected]) => {
    try {
      const { data, headers } = await call("post", "/admin/products/slides-export", { ids }, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "slides.xlsx";
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
      a.click();
      toast.success(`Excel para slides descargado (${headers["x-rows"] || "?"} filas). Impórtalo en el generador de slides.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o referencia" className="field !h-10 !w-56" data-testid="admin-products-search" />
          <select value={section} onChange={(e) => setSection(e.target.value)} className={sel} data-testid="admin-products-section">
            <option value="all">Web y outlet ({list.length})</option>
            <option value="shop">Solo web ({n((p) => !p.outlet)})</option>
            <option value="outlet">Solo outlet ({n((p) => p.outlet)})</option>
          </select>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={sel} data-testid="admin-products-category">
            <option value="all">Todas las secciones</option>
            {CATS.map(([id, label]) => <option key={id} value={id}>{label} ({n((p) => inSection(p) && p.category === id)})</option>)}
          </select>
          <select value={view} onChange={(e) => setView(e.target.value)} className={sel} data-testid="admin-products-view">
            <option value="all">Todos ({n((p) => inSection(p) && inCat(p))})</option>
            <option value="visible">Visibles ({n((p) => inSection(p) && inCat(p) && p.active)})</option>
            <option value="hidden">Ocultos ({n((p) => inSection(p) && inCat(p) && !p.active)})</option>
          </select>
          <span className="text-xs text-slate-500" data-testid="admin-products-count">{shown.length} artículos</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {selected.size > 0 ? (
            <>
              <button className="btn-primary !h-10 !bg-verde hover:!bg-verde/90" onClick={() => exportSlides()} data-testid="admin-slides-export-button"><Download className="w-4 h-4 mr-2" /> Excel para slides ({selected.size})</button>
              <button className="btn-outline !h-10" onClick={() => setSelected(new Set())} data-testid="admin-slides-clear-button">Quitar selección</button>
            </>
          ) : (
            <button className="btn-outline !h-10" onClick={() => exportSlides(shown.filter((p) => p.active).map((p) => p.id))} title="Todos los artículos visibles con el filtro actual (web y outlet, nuevos y rebajados)" data-testid="admin-slides-export-all-button"><Download className="w-4 h-4 mr-2" /> Excel slides: todo ({shown.filter((p) => p.active).length})</button>
          )}
          <label className="btn-outline !h-10 cursor-pointer" data-testid="admin-import-articles-label">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar artículos
            <input type="file" accept=".xlsx" className="hidden" onChange={importXlsx("/admin/import-articles", "artículos")} data-testid="admin-import-articles-input" />
          </label>
          <label className="btn-outline !h-10 cursor-pointer" data-testid="admin-import-label">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar descripciones
            <input type="file" accept=".xlsx" className="hidden" onChange={importXlsx("/admin/import-descriptions", "descripciones")} data-testid="admin-import-input" />
          </label>
          <button className="btn-primary !h-10" onClick={() => setEditing("new")} data-testid="admin-new-product-button"><Plus className="w-4 h-4" /> Nuevo producto</button>
        </div>
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="p-3 w-10"><input type="checkbox" checked={allShownSelected} onChange={toggleAll} title="Seleccionar todos los que se ven" className="w-4 h-4 accent-[#0D1F7A]" data-testid="admin-select-all" /></th><th className="p-3">Producto</th><th className="p-3">Categoría</th><th className="p-3">Precio</th><th className="p-3">Stock</th><th className="p-3">Estado</th><th className="p-3" /></tr>
          </thead>
          <tbody className="divide-y">
            {shown.map((p) => {
              const stock = p.sizes.reduce((s, x) => s + x.stock, 0);
              return (
                <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className={selected.has(p.id) ? "bg-emerald-50/60" : ""}>
                  <td className="p-3"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} className="w-4 h-4 accent-[#0D1F7A]" data-testid={`admin-select-${p.id}`} /></td>
                  <td className="p-3 flex items-center gap-3"><img src={imgSrc(p.images[0], 200)} alt="" loading="lazy" className="w-10 h-12 object-cover bg-slate-100" /><span><span className="font-semibold">{p.name}</span>{p.reference && <span className="block text-[11px] font-mono text-slate-500">{p.reference}</span>}</span></td>
                  <td className="p-3">{CATEGORIES.find((c) => c.id === p.category)?.label}</td>
                  <td className="p-3 font-semibold">{euro(p.price)}</td>
                  <td className={`p-3 font-mono ${stock <= 3 ? "text-brand font-bold" : ""}`} title={p.sizes.map((s) => `${s.size}${s.color ? ` ${s.color}` : ""}: ${s.stock}`).join(" · ")}>{stock}</td>
                  <td className="p-3">{p.active ? <span className="tag bg-emerald-100 text-emerald-700">Visible</span> : <span className="tag bg-slate-100 text-slate-500">Oculto</span>}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button className={`p-2 hover:text-amber-500 ${p.featured ? "text-amber-500" : "text-slate-300"}`} onClick={() => toggleStar(p)} title={p.outlet ? "Destacar: sale el primero en el outlet" : "Destacar en portada"} data-testid={`admin-star-${p.id}`}><Star className="w-4 h-4" fill={p.featured ? "currentColor" : "none"} /></button>
                    <button className="p-2 hover:text-brand" onClick={() => setEditing(p)} data-testid={`admin-edit-${p.id}`}><Edit className="w-4 h-4" /></button>
                    <button className="p-2 hover:text-brand" onClick={() => setToDelete(p)} data-testid={`admin-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <ProductForm key={editing.id || "new"} open product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar "{toDelete?.name}"?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="admin-delete-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="admin-delete-confirm">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
